import {
  GetViewerTranscriptRequestsQuery,
  TranscriptRecipientType,
  TranscriptRequestStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { Button } from '@sandtech-hub-monorepo/ui-kit';
import { Download } from 'lucide-react';
import { RequestPrefill } from './RequestOfficialTranscriptModal';
import RequestStatusPill from './RequestStatusPill';
import TranscriptStepper from './TranscriptStepper';
import { StepState } from './StepIcon';

type ViewerTranscriptRequest =
  GetViewerTranscriptRequestsQuery['viewer']['transcriptRequests'][number];

interface Props {
  request: ViewerTranscriptRequest;
  /** Opens the request modal prefilled from a rejected request. */
  onResubmit: (prefill: RequestPrefill) => void;
}

const RECIPIENT_TYPE_LABELS: Record<TranscriptRecipientType, string> = {
  [TranscriptRecipientType.UniversityCollege]: 'University',
  [TranscriptRecipientType.EmployerOrganization]: 'Employer',
  [TranscriptRecipientType.Personal]: 'Personal',
  [TranscriptRecipientType.Other]: 'Other',
};

const stepsFor = (
  status: TranscriptRequestStatus
): { label: string; state: StepState }[] => {
  switch (status) {
    case TranscriptRequestStatus.Approved:
      return [
        { label: 'Submitted', state: 'done' },
        { label: 'Approved', state: 'done' },
        { label: 'Sent to recipient', state: 'done' },
      ];
    case TranscriptRequestStatus.Rejected:
      return [
        { label: 'Submitted', state: 'done' },
        { label: 'Reviewed', state: 'done' },
        { label: 'Rejected', state: 'failed' },
      ];
    default:
      return [
        { label: 'Submitted', state: 'done' },
        { label: 'Under review', state: 'current' },
        { label: 'Sent to recipient', state: 'pending' },
      ];
  }
};

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const TranscriptRequestCard = ({ request, onResubmit }: Props) => {
  const recipient = request.recipients[0];
  const issued = request.issuedTranscripts[0];

  return (
    <div className="border border-border rounded-xl p-4 md:p-5 flex flex-col gap-4 bg-background">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-foreground truncate">
            {recipient?.institutionName ?? 'Recipient'}
          </p>
          <p className="text-xs text-muted-foreground">
            {recipient
              ? `${RECIPIENT_TYPE_LABELS[recipient.recipientType]} · `
              : ''}
            Request #TR-{request.requestNumber} · Requested{' '}
            {formatDate(request.createdAt)}
          </p>
        </div>
        <RequestStatusPill status={request.status} />
      </div>

      <TranscriptStepper steps={stepsFor(request.status)} />

      {request.status === TranscriptRequestStatus.Approved && issued && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Secure link sent to{' '}
            <span className="font-medium text-foreground">
              {recipient?.email}
            </span>
            {issued.expiresAt && (
              <>
                {' '}
                · expires on{' '}
                <span className="font-medium text-foreground">
                  {formatDate(issued.expiresAt)}
                </span>
              </>
            )}
          </p>
          {issued.documentDownloadUrl && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="rounded-full gap-2 shrink-0"
            >
              <a href={issued.documentDownloadUrl} download>
                <Download className="w-4 h-4" />
                Download PDF
              </a>
            </Button>
          )}
        </div>
      )}

      {request.status === TranscriptRequestStatus.Rejected && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-destructive">
            Reason: {request.decisionReason ?? '—'}
          </p>
          {recipient && (
            <Button
              className="rounded-full self-end sm:self-auto"
              onClick={() =>
                onResubmit({
                  recipientType: recipient.recipientType,
                  institutionName: recipient.institutionName,
                  email: recipient.email,
                  note: request.note ?? '',
                })
              }
            >
              Resubmit
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TranscriptRequestCard;
